import { useMemo } from "react";

import { Navigate, useLocation, useParams } from "react-router-dom";

import { type Label, type NavSection, type SidebarAnchor, type SidebarNode } from "../../types/content.ts";
import DocsLayout from "../layout/DocsLayout.jsx";
import { sectionConfig } from "../lib/content.js";
import { useLocale } from "../lib/i18n.jsx";
import { useHashScroll, useScrollSpy } from "../lib/useScrollSpy.js";
import { groupOperations } from "../openapi/groupOperations.js";
import { type OperationGroup } from "../openapi/groupOperations.ts";
import Reference from "../openapi/Reference.jsx";
import { type NormalizedSpec, type Operation } from "../openapi/types.ts";
import useOpenApi from "../openapi/useOpenApi.js";
import { ReferenceSkeleton } from "../theme/Skeleton.jsx";

// Agrupa uma vez e devolve as duas visões que precisam concordar: a ordem em
// que os endpoints são renderizados e as entradas do índice lateral.
//
// Elas têm de sair da MESMA lista. Se o índice fosse agrupado e o conteúdo
// seguisse a ordem do spec, o scroll-spy destacaria itens saltando de um grupo
// para outro conforme a página rola — o índice pareceria embaralhado.
function buildSections(operations: Operation[], config: NavSection): OperationGroup[] {
  if (config.groupOperations === false) return [{ key: null, label: null, operations }];
  return groupOperations(operations);
}

// Entradas `kind: "group"` são cabeçalhos de recurso, não navegáveis.
function buildToc(sections: OperationGroup[], config: NavSection, activeAnchor: string | null): SidebarAnchor[] {
  const usePath = config.operationLabel === "path";
  return sections.flatMap((section): SidebarAnchor[] => [
    ...(section.label
      ? [{ kind: "group" as const, id: `sub-${section.key}`, label: section.label, active: false }]
      : []),
    ...section.operations.map(
      (op): SidebarAnchor => ({
        kind: "op",
        id: op.id,
        // O summary do spec descreve o que o endpoint faz; o path repete o que o
        // grupo já disse. Sem summary, o path continua sendo o único rótulo real.
        label: usePath ? op.path : op.title || op.path,
        method: op.method,
        path: op.path,
        active: activeAnchor === op.id,
      })
    ),
  ]);
}

// Distribui as tags do spec nos grupos declarados em docs.config. Tag que
// não foi mencionada em nenhum grupo cai em "Outros" em vez de desaparecer:
// endpoint novo no backend tem que aparecer na documentação sozinho.
function buildGroups(
  spec: NormalizedSpec,
  config: NavSection,
  locale: string,
  activeTagSlug: string | undefined,
  toc: SidebarAnchor[],
  tagLabel: (tag: string) => string
): SidebarNode[] {
  const byName = new Map(spec.groups.map((g) => [g.name, g]));
  const claimed = new Set<string>();
  const sectionId = config.id;

  const toItem = (group: NormalizedSpec["groups"][number]) => ({
    key: group.id,
    label: tagLabel(group.name),
    to: `/${locale}/${sectionId}/${group.id}`,
    active: group.id === activeTagSlug,
    children: group.id === activeTagSlug ? toc : null,
  });

  const groups = (config.groups || []).map((g) => {
    const items = g.tags
      .map((name) => byName.get(name))
      .filter((grupo) => grupo !== undefined)
      .map((grupo) => {
        claimed.add(grupo.name);
        return toItem(grupo);
      });
    return { id: g.tags.join("-"), label: g.label, items };
  });

  const rest = spec.groups.filter((g) => !claimed.has(g.name));
  if (rest.length) {
    groups.push({
      id: "__outras",
      label: { pt: "Outros", en: "Other", es: "Otros" },
      items: rest.map(toItem),
    });
  }

  return groups.filter((g) => g.items.length);
}

export default function ApiPage() {
  const { locale, section, slug } = useParams();
  const { hash } = useLocation();
  const { t, tagLabel } = useLocale();
  const { spec, loading, error } = useOpenApi();

  const config = sectionConfig(section);
  const group = spec?.groups.find((g) => g.id === slug) || null;

  const operations = group?.operations || [];

  const sections = useMemo(() => (config ? buildSections(operations, config) : []), [operations, config]);
  const ordered = useMemo(() => sections.flatMap((s) => s.operations), [sections]);

  const activeAnchor = useScrollSpy(ordered.map((op) => op.id));

  const toc = useMemo(() => (config ? buildToc(sections, config, activeAnchor) : []), [sections, config, activeAnchor]);

  const sidebar = useMemo(
    () => (spec && config ? buildGroups(spec, config, locale ?? "", slug, toc, tagLabel) : []),
    [spec, config, locale, slug, toc, tagLabel]
  );

  // O conteúdo é montado depois do fetch do spec, então a âncora do link
  // compartilhado ainda não existe no DOM quando o navegador tenta pular.
  useHashScroll(hash, !!group);

  if (loading) {
    return (
      <DocsLayout activeSection={section}>
        <ReferenceSkeleton />
      </DocsLayout>
    );
  }

  if (error) {
    return (
      <DocsLayout activeSection={section}>
        <h1 className="doc-title">{t("specError")}</h1>
        <pre className="doc-code">
          <code>{String(error.message || error)}</code>
        </pre>
      </DocsLayout>
    );
  }

  // `loading` e `error` já saíram acima. Sem spec não há o que desenhar — e
  // dizer isso explicitamente é melhor do que o resto do componente presumir
  // que o hook nunca devolve os três vazios ao mesmo tempo.
  if (!spec) {
    return (
      <DocsLayout activeSection={section}>
        <p className="doc-p">{t("notFound")}</p>
      </DocsLayout>
    );
  }

  // /:locale/api sem tag → primeira tag na ordem de docs.config.
  if (!slug) {
    const first = spec.groups[0];
    return first ? (
      <Navigate to={`/${locale}/${section}/${first.id}`} replace />
    ) : (
      <DocsLayout activeSection={section}>
        <p className="doc-p">{t("notFound")}</p>
      </DocsLayout>
    );
  }

  return (
    <DocsLayout activeSection={section} sidebar={sidebar} contentKey={`${locale}/${section}/${slug}`}>
      {group ? (
        <Reference
          group={group}
          operations={ordered}
          baseUrl={spec.baseUrl}
          baseUrlVars={spec.baseUrlVars}
          serverDescription={spec.serverDescription}
          fieldsOf={spec.fieldsOf}
        />
      ) : (
        <p className="doc-p">{t("notFound")}</p>
      )}
    </DocsLayout>
  );
}
